import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';

/**
 * NumericInput - Componente base para inputs numéricos en toda la plataforma
 * 
 * Comportamiento:
 * - Inicia vacío (placeholder visible)
 * - No fuerza '0' al borrar
 * - No permite caracteres no numéricos
 * - Soporta formato de moneda (opcional)
 */
const NumericInput = ({
    value,
    onChange,
    placeholder = "0",
    min = 0,
    max,
    className,
    prefix,
    suffix,
    allowDecimals = false,
    ...props
}) => {
    // Estado interno para manejar el valor como string y permitir vacíos
    const [displayValue, setDisplayValue] = useState('');

    // Sincronizar con props externas
    useEffect(() => {
        if (value === null || value === undefined || value === '') {
            setDisplayValue('');
        } else {
            setDisplayValue(value.toString());
        }
    }, [value]);

    const handleChange = (e) => {
        const newValue = e.target.value;

        // Permitir vacío
        if (newValue === '') {
            setDisplayValue('');
            onChange(''); // Propagar vacío al padre
            return;
        }

        // Validar formato numérico
        const regex = allowDecimals ? /^\d*\.?\d*$/ : /^\d*$/;

        if (regex.test(newValue)) {
            // Validar maximo si existe
            if (max !== undefined && parseFloat(newValue) > max) {
                return;
            }

            // Validar minimo solo si no se esta escribiendo (para no bloquear)
            // Aquí permitimos escribir libremente y el padre debería validar en blur o submit si es critico

            setDisplayValue(newValue);

            // Propagar al padre el valor numérico o string según se prefiera. 
            // Para consistencia con HTML input type="number", propagamos el string, 
            // pero el componente padre debe manejarlo.
            onChange(newValue);
        }
    };

    return (
        <div className="relative w-full">
            {prefix && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                    {prefix}
                </span>
            )}
            <Input
                {...props}
                type="text" // Usamos text para control total del formato
                inputMode={allowDecimals ? "decimal" : "numeric"}
                value={displayValue}
                onChange={handleChange}
                placeholder={placeholder}
                className={`${className} ${prefix ? 'pl-7' : ''} ${suffix ? 'pr-8' : ''}`}
            />
            {suffix && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                    {suffix}
                </span>
            )}
        </div>
    );
};

export default NumericInput;
